import { LoginBox } from "../components/login-box";
import LoginBoxNew from "../components/login-new/login-new";
import { App, UniversalAppState } from "./app";
import { Cache } from "./cache";
import { EventEmitter } from "./event-emitter";
import { Plugin } from "./plugin";

export interface LoginPluginPartialAppState<UserProfile> {
  isLoggedIn: boolean;
  userProfile?: UserProfile;
}

const DEFAULT_INTERVAL_TO_CHECK_FOR_ENDED_SESSIONS = 30_000;

/**
 * Plugin that manages logging in with our [Strapi Keycloak plugin](https://gitlab.com/hipsquare/strapi-plugin-keycloak).
 *
 * It enforces that the user is logged in. It creates a /login route with a login button
 * and redirects logged-out users there.
 *
 * When clicking the login button, the login process is initialized.
 *
 * The plugin also populates the `isLoggedIn` and `userProfile` state keys so the app can always get the current login
 * state and profile.
 */
export class LoginPlugin<
    UserProfile,
    AppState extends LoginPluginPartialAppState<UserProfile> &
      UniversalAppState,
  >
  extends EventEmitter<"login-status-changed", { isLoggedIn: boolean }>
  implements Plugin<AppState, LoginPluginPartialAppState<UserProfile>>
{
  private readonly _cache: Cache;
  private _app?: App<AppState>;
  private readonly _initialState: LoginPluginPartialAppState<UserProfile> = {
    isLoggedIn: false,
  };
  detectEndedSessionIntervalHandler?: ReturnType<typeof setInterval>;

  constructor(
    private readonly _postLoginPath: string,
    private readonly _strapiUrl: string,
    private readonly _pageTitle: string,
    private readonly _options?: {
      automaticallyDetectEndedSessions?: boolean;
      automaticallyDetectEndedSessionsIntervalMs?: number;
      useNewLoginBoxDesign?: boolean;
      logoSrc?: string;
      welcomeText?: string;
      buttonText?: string;
      // Added option for backend login redirect
      redirectToBackendLoginIfLoggedOut?: boolean;
      // Whitelist of URLs that don't require authentication
      whitelistUrls?: string[];
      // Option to disable frontend login route creation
      createLoginRoute?: boolean;
    }
  ) {
    super();
    this._cache = new Cache(
      this._options?.automaticallyDetectEndedSessionsIntervalMs ||
        DEFAULT_INTERVAL_TO_CHECK_FOR_ENDED_SESSIONS
    );
  }

  async onRegister(app: App<AppState>) {
    this._app = app;
    const view = app.createView({ name: "login" });

    if (!this._options?.redirectToBackendLoginIfLoggedOut) {
      const loginBox = view.addComponent({
        component: this._options?.useNewLoginBoxDesign ? LoginBoxNew : LoginBox,
      });
      loginBox.mapState(() => ({
        title: this._pageTitle,
        welcomeText: this._options?.welcomeText,
        buttonText: this._options?.buttonText ?? "Login",
        logoSrc: this._options?.logoSrc,
      }));
      loginBox.on(
        "component:click",
        () =>
          (window.location.href = `${this._strapiUrl}/keycloak/login?redirectTo=${window.location.origin}/callback`) as unknown as void
      );
    }

    // Only create login route if not explicitly disabled
    if (this._options?.createLoginRoute !== false) {
      app.createRoute({
        path: "/login",
        view: view,
        isVisible: true,
        isOverlayChild: false,
      });
    }

    app.on(
      "router:route-loaded",
      async (evt: { payload?: { path?: string } }) => {
        const path = evt.payload?.path || "/";

        if (path === "/callback") {
          const cacheUrl = `${this._strapiUrl}/keycloak/isLoggedIn`;

          this._cache.clearCacheForUrl(cacheUrl);
        }

        const forceFresh = path === "/callback";

        const loggedIn: boolean = await this.isLoggedIn(forceFresh);

        switch (path) {
          case "/callback": {
            console.log("[LoginPlugin] Handling /callback route");
            // Use the already-fresh login check (cache was cleared above)
            const freshLoggedIn = loggedIn;
            await this.updateLoginState(freshLoggedIn);
            if (freshLoggedIn) {
              this._app?.setAppState({
                ...this._app?.getAppState(),
                isLoading: false,
              });
              this._app?.navigate(this._postLoginPath);
              this.fireEvent("login-status-changed", { isLoggedIn: true });
            } else {
              this._app?.setAppState({
                ...this._app?.getAppState(),
                isLoading: false,
              });
              // Redirect to backend login if configured, otherwise navigate to /login
              // This prevents staying on /callback and causing infinite refresh loops
              if (this._options?.redirectToBackendLoginIfLoggedOut) {
                const redirectUrl = `${this._strapiUrl}/keycloak/login?redirectTo=${window.location.origin}/callback`;

                window.location.href = redirectUrl;
              } else {
                this._app?.navigate("/login");
              }
            }
            break;
          }
          default: {
            const currentPath = path;
            const isWhitelisted = this.isUrlWhitelisted(currentPath);

            // If URL is whitelisted, allow access without authentication
            if (isWhitelisted) {
              this._app?.setAppState({
                ...this._app?.getAppState(),
                isLoading: false,
              });
              return;
            }

            if (
              evt.payload?.path !== "/login" &&
              !loggedIn &&
              this._options?.redirectToBackendLoginIfLoggedOut
            ) {
              // Redirect to backend login
              const redirectUrl = `${this._strapiUrl}/keycloak/login?redirectTo=${window.location.origin}/callback`;

              window.location.href = redirectUrl;
            } else if (
              evt.payload?.path === "/login" &&
              !loggedIn &&
              this._options?.redirectToBackendLoginIfLoggedOut
            ) {
              const redirectUrl = `${this._strapiUrl}/keycloak/login?redirectTo=${window.location.origin}/callback`;

              window.location.href = redirectUrl;
            } else if (evt.payload?.path !== "/login" && !loggedIn) {
              this._app?.navigate("/login");
              this._app?.setAppState({
                ...this._app?.getAppState(),
                isLoading: false,
              });
            } else if (evt.payload?.path === "/login" && loggedIn) {
              this._app?.navigate("/");
            } else if (evt.payload?.path === "/login" && !loggedIn) {
              this._app?.setAppState({
                ...this._app.getAppState(),
                isLoading: false,
              });
            } else {
            }
          }
        }
      }
    );

    const callbackView = app.createView({ name: "callback" });

    app.createRoute({
      path: "/callback",
      view: callbackView,
      isVisible: true,
      isOverlayChild: false,
    });

    // If we're on /callback during initialization, clear cache to ensure fresh check
    // This is important when navigating from one client (home-portal) to another (product-test)
    // where cookies might have been set by the OAuth callback
    const currentPath = window.location.pathname;
    if (currentPath === "/callback") {
      const cacheUrl = `${this._strapiUrl}/keycloak/isLoggedIn`;

      this._cache.clearCacheForUrl(cacheUrl);
      console.log("[LoginPlugin] Initialization: Forcing fresh login check");
    }

    const loggedIn = await this.isLoggedIn(currentPath === "/callback");

    if (loggedIn) {
      this.setupIntervalToDetectEndedSessions();
    }

    await this.updateLoginState(loggedIn);

    if (loggedIn) {
      this.fireEvent("login-status-changed", { isLoggedIn: true });
    }

    // redirect to login page if user is not logged in and current path is not whitelisted
    if (!loggedIn) {
      const currentPath = window.location.pathname;
      const isWhitelisted = this.isUrlWhitelisted(currentPath);

      if (!isWhitelisted) {
        if (this._options?.redirectToBackendLoginIfLoggedOut) {
          const redirectUrl = `${this._strapiUrl}/keycloak/login?redirectTo=${window.location.origin}/callback`;

          window.location.href = redirectUrl;
        } else {
          this._app.navigate("/login");
        }
      } else {
        this._app.setAppState({
          ...this._app.getAppState(),
          isLoading: false,
        });
      }
    }
  }

  private setupIntervalToDetectEndedSessions() {
    if (!this._options?.automaticallyDetectEndedSessions) {
      return;
    }

    this.detectEndedSessionIntervalHandler = setInterval(() => {
      void (async () => {
        const loggedIn = await this.isLoggedIn();
        if (!loggedIn) {
          this._app?.navigate("/login");
          this.destroyIntervalToDetectEndedSessions();
        }
      })();
    }, this._options.automaticallyDetectEndedSessionsIntervalMs || DEFAULT_INTERVAL_TO_CHECK_FOR_ENDED_SESSIONS);
  }

  private destroyIntervalToDetectEndedSessions() {
    if (this.detectEndedSessionIntervalHandler) {
      clearInterval(this.detectEndedSessionIntervalHandler);
    }
  }

  isUrlWhitelisted(path: string): boolean {
    const whitelistUrls = this._options?.whitelistUrls || [];
    return whitelistUrls.includes(path);
  }

  getInitialState() {
    return this._initialState;
  }

  async isLoggedIn(forceFreshCheck?: boolean): Promise<boolean> {
    try {
      const url = `${this._strapiUrl}/keycloak/isLoggedIn`;

      const response = await this._cache.fetch(
        url,
        {
          credentials: "include",
        },
        forceFreshCheck
      );

      const result = (await response.json()) as boolean;

      return result;
    } catch (error) {
      console.error("[LoginPlugin] Error checking login status:", error);
      return false;
    }
  }

  private async updateLoginState(loggedIn: boolean) {
    let userProfile;
    if (loggedIn) {
      userProfile = await this.getUserProfile();
    }

    this._app?.setAppState({
      ...this._app.getAppState(),
      isLoggedIn: loggedIn,
      userProfile,
    });
  }

  private async getUserProfile(): Promise<unknown> {
    const response = await this._cache.fetch(
      `${this._strapiUrl}/keycloak/profile`,
      {
        credentials: "include",
      }
    );

    return (await response.json()) as Promise<unknown>;
  }

  logout() {
    window.location.href = `${this._strapiUrl}/keycloak/logout`;
  }
}
